import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { Card, CardContent } from '@dashboard/components/ui/card';
import { useStaffInviteCounts } from '../hooks/useStaffInviteCounts';
import { STATUS_LABEL } from './staffColumns';

interface Props {
  pageId: string;
}

const manageHref = (pageId: string, status?: string) =>
  status ? `/pages/${pageId}/staff/manage?status=${status}` : `/pages/${pageId}/staff/manage`;

const PILL_CONFIG = [
  { key: 'pending', urlSlug: 'invited', dotClass: 'bg-muted-foreground' },
  { key: 'active', urlSlug: 'accepted', dotClass: 'bg-green-500' },
] as const;

const StaffSummaryCard: React.FC<Props> = ({ pageId }) => {
  const { data } = useStaffInviteCounts(pageId);
  if (!data || data.total === 0) return null;

  // Deleted memberships are intentionally omitted — they don't show on
  // the manage table under the default Status filter, so listing them
  // here would mismatch.
  const pills = PILL_CONFIG.map(p => ({ ...p, count: data[p.key] })).filter(p => p.count > 0);

  return (
    <Card className="mt-4">
      <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="font-semibold">{data.total} staff invited</span>
          {pills.map(p => (
            <Link
              key={p.key}
              to={manageHref(pageId, p.urlSlug)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 text-xs hover:border-foreground hover:text-foreground transition-colors"
            >
              {'dotClass' in p && <span className={`size-1.5 rounded-full ${p.dotClass}`} />}
              {p.count} {STATUS_LABEL[p.key]}
            </Link>
          ))}
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={manageHref(pageId)}>
            Manage staff
            <ArrowRight className="size-3.5 ml-1" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default StaffSummaryCard;
